import { useState } from 'react';
import { CalendarClock, CalendarX2 } from 'lucide-react';
import { Modal } from '../../../../components/common/Modal';
import { useLanguage } from '../../../../features/language/useLanguage';
import type { CreateVoucherInput } from '../../../../services/subscriptionService';
import { openNativeDatePickerSafely, preventManualDateEntry, preventManualDatePasteOrDrop } from '../../../../utils/date';
import { Field } from './primitives';

interface CreateVoucherModalProps {
  onClose: () => void;
  onSubmit: (input: CreateVoucherInput) => void;
  isPending: boolean;
}

/** Modal form for creating a new voucher. */
export function CreateVoucherModal({ onClose, onSubmit, isPending }: CreateVoucherModalProps) {
  const { t } = useLanguage();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [months, setMonths] = useState(1);
  const [expiresAt, setExpiresAt] = useState('');
  const [maxRedemptions, setMaxRedemptions] = useState('');

  const handleSubmit = () => {
    if (!name.trim() || !expiresAt) return;
    onSubmit({
      name: name.trim(),
      code: code.trim() || undefined,
      duration_months: months,
      expires_at: new Date(expiresAt).toISOString(),
      max_redemptions: maxRedemptions ? parseInt(maxRedemptions, 10) : undefined,
    });
  };

  const isValid = name.trim() && expiresAt;

  return (
    <Modal isOpen onClose={onClose} title={t('subscription.voucher.create_title')} maxWidth="md">
      <div className="space-y-4">
        <Field label={t('subscription.voucher.field.name')}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder={t('subscription.voucher.field.name')}
            data-test-id="voucher-field-name"
          />
        </Field>

        <Field label={t('subscription.voucher.field.code_optional')}>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono uppercase tracking-[0.15em] text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="XXXX-XXXX-XXXX"
            data-test-id="voucher-field-code"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('subscription.voucher.field.months')}>
            <div className="relative">
              <CalendarClock className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
              <input
                type="number"
                min={1}
                value={months}
                onChange={(e) => setMonths(parseInt(e.target.value || '1', 10))}
                className="w-full rounded-md border border-gray-300 pl-8 pr-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                data-test-id="voucher-field-months"
              />
            </div>
          </Field>
          <Field label={t('subscription.voucher.col.expires')}>
            <div className="relative">
              <CalendarX2 className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
              <input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                onClick={openNativeDatePickerSafely}
                onKeyDown={preventManualDateEntry}
                onPaste={preventManualDatePasteOrDrop}
                onDrop={preventManualDatePasteOrDrop}
                className="w-full rounded-md border border-gray-300 pl-8 pr-3 py-2 text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 [&::-webkit-calendar-picker-indicator]:hidden"
                data-test-id="voucher-field-expires"
              />
            </div>
          </Field>
        </div>

        <Field label={t('subscription.voucher.field.max_redemptions')}>
          <input
            type="number"
            min={1}
            value={maxRedemptions}
            onChange={(e) => setMaxRedemptions(e.target.value)}
            placeholder={t('subscription.voucher.unlimited')}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            data-test-id="voucher-field-max-redemptions"
          />
        </Field>

        <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isPending || !isValid}
            className="px-4 py-2 rounded-md bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium shadow-sm transition-colors"
            data-test-id="create-voucher-submit"
          >
            {isPending ? t('common.loading') : t('common.create')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
